import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, 
  Store, 
  ShoppingCart,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [accessStatus, setAccessStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Request access modal
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [companyRegNo, setCompanyRegNo] = useState('');
  const [requesting, setRequesting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, accessRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/user-access-status`, { headers })
      ]);
      
      setStats(statsRes.data);
      setAccessStatus(accessRes.data);
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestAccess = async () => {
    if (!companyRegNo.trim()) {
      toast.error('Please enter company registration number');
      return;
    }
    
    setRequesting(true);
    try {
      await axios.post(`${API}/access-requests`, 
        { company_reg_no: companyRegNo },
        { headers: getAuthHeader() }
      );
      toast.success('Access request sent!');
      setRequestModalOpen(false);
      setCompanyRegNo('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequesting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="status-approved">Approved</Badge>;
      case 'pending':
        return <Badge className="status-pending">Pending</Badge>;
      case 'rejected':
        return <Badge className="status-rejected">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Group outlets by status
  const approvedOutlets = accessStatus.filter(a => a.status === 'approved');
  const pendingOutlets = accessStatus.filter(a => a.status === 'pending');
  const rejectedOutlets = accessStatus.filter(a => a.status === 'rejected');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="user-dashboard">
      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl font-bold">Welcome, {user?.first_name}!</h1>
        <p className="text-muted-foreground mt-1">
          {user?.status === 'pending' 
            ? 'Your account is pending approval. Request access to an outlet to get started.'
            : 'View your assigned outlets and manage deliveries.'
          }
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="metric-card" data-testid="stat-approved">
          <div className="flex items-center justify-between">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span className="text-2xl font-bold">{stats?.approved_outlets || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Approved Outlets</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-orders">
          <div className="flex items-center justify-between">
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
            <span className="text-2xl font-bold">{stats?.total_orders || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Total Orders</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-my-orders">
          <div className="flex items-center justify-between">
            <Store className="h-5 w-5 text-primary" />
            <span className="text-2xl font-bold">{stats?.my_orders || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">My Orders</p>
        </Card>
      </div>

      {/* Request Access Button */}
      <div className="flex justify-end">
        <Button onClick={() => setRequestModalOpen(true)} data-testid="request-access-btn">
          <Send className="h-4 w-4 mr-2" />
          Request Outlet Access
        </Button>
      </div>

      {/* Approved Outlets */}
      {approvedOutlets.length > 0 && (
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            Your Outlets
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {approvedOutlets.map((access) => (
              <Card 
                key={access.outlet_id} 
                className="card-hover cursor-pointer"
                onClick={() => navigate(`/orders/${access.outlet_id}`)}
                data-testid={`outlet-${access.outlet_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{access.outlet_name}</h3>
                      {access.company_reg_no && (
                        <p className="text-sm font-mono text-muted-foreground">
                          #{access.company_reg_no}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(access.status)}
                  </div>
                  
                  <Button className="w-full">
                    View Orders
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Pending Requests */}
      {pendingOutlets.length > 0 && (
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Pending Requests
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingOutlets.map((access) => (
              <Card key={access.outlet_id} className="opacity-75" data-testid={`pending-${access.outlet_id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{access.outlet_name}</h3>
                      {access.company_reg_no && (
                        <p className="text-sm font-mono text-muted-foreground">
                          #{access.company_reg_no}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(access.status)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    Waiting for admin approval...
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Rejected Requests */}
      {rejectedOutlets.length > 0 && (
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            Rejected Requests
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rejectedOutlets.map((access) => (
              <Card key={access.outlet_id} className="opacity-50" data-testid={`rejected-${access.outlet_id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{access.outlet_name}</h3>
                      {access.company_reg_no && (
                        <p className="text-sm font-mono text-muted-foreground">
                          #{access.company_reg_no}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(access.status)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {accessStatus.length === 0 && (
        <Card className="p-12 text-center">
          <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Outlet Access</h3>
          <p className="text-muted-foreground mb-4">
            Request access to an outlet using the company registration number to get started.
          </p>
          <Button onClick={() => setRequestModalOpen(true)}>
            <Send className="h-4 w-4 mr-2" />
            Request Access
          </Button>
        </Card>
      )}

      {/* Request Access Modal */}
      <Dialog open={requestModalOpen} onOpenChange={setRequestModalOpen}>
        <DialogContent data-testid="request-modal">
          <DialogHeader>
            <DialogTitle>Request Outlet Access</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter the company registration number of the outlet you want to join.
              The outlet admin will review your request.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="company-reg">Company Registration Number</Label>
              <Input
                id="company-reg"
                value={companyRegNo}
                onChange={(e) => setCompanyRegNo(e.target.value)}
                placeholder="e.g., ABC123"
                data-testid="company-reg-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRequestAccess} disabled={requesting} data-testid="send-request-btn">
              {requesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
